import React, { useEffect, useState } from 'react';
import { Button, Toggle } from '@/components';
import SideBarModal from '@/components/Modal/SideBarModal';
import { BUSINESS_UNIT_LABELS, BusinessUnit } from '@/types/common';
import {
  ALERT_TYPE_DESCRIPTIONS,
  ALERT_TYPE_LABELS,
  AlertAssignee,
  AlertType,
} from '@/services/api/monitoring';
import {
  FieldError,
  FieldHelper,
  FieldLabel,
  inputClass,
  SelectField,
} from '@/components';

export interface AlertAssigneeFormValues {
  alertType: AlertType;
  businessUnit: string;
  slackUserID: string;
  slackDisplayName: string;
  isActive: boolean;
}

// Slack member IDs: U or W followed by upper-case alphanumerics. A handle
// (@andi) or a display name would render as literal text in the message.
export const SLACK_USER_ID_PATTERN = /^[UW][A-Z0-9]{6,}$/;

export const validateAlertAssignee = (
  values: AlertAssigneeFormValues
): Partial<Record<'slackUserID' | 'slackDisplayName', string>> => {
  const errors: Partial<Record<'slackUserID' | 'slackDisplayName', string>> =
    {};
  if (!SLACK_USER_ID_PATTERN.test(values.slackUserID.trim())) {
    errors.slackUserID =
      'Pakai Slack member ID (contoh U0123ABCD), bukan @username.';
  }
  if (!values.slackDisplayName.trim()) {
    errors.slackDisplayName = 'Isi nama yang tampil.';
  }
  return errors;
};

const EMPTY: AlertAssigneeFormValues = {
  alertType: AlertType.RENT_SHUTDOWN_PENDING,
  businessUnit: '',
  slackUserID: '',
  slackDisplayName: '',
  isActive: true,
};

export interface AlertAssigneeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: AlertAssigneeFormValues) => Promise<void>;
  isSaving: boolean;
  /** Edit when set; create otherwise. */
  existing: AlertAssignee | null;
  alertTypes: AlertType[];
  /** Pre-selected type for a create opened from a filtered list. */
  defaultAlertType?: AlertType;
}

export const AlertAssigneeModal: React.FC<AlertAssigneeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSaving,
  existing,
  alertTypes,
  defaultAlertType,
}) => {
  const [values, setValues] = useState<AlertAssigneeFormValues>(EMPTY);
  const [errors, setErrors] = useState<
    ReturnType<typeof validateAlertAssignee>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setValues(
      existing
        ? {
            alertType: existing.alertType as AlertType,
            businessUnit: existing.businessUnit ?? '',
            slackUserID: existing.slackUserID,
            slackDisplayName: existing.slackDisplayName,
            isActive: existing.isActive,
          }
        : { ...EMPTY, alertType: defaultAlertType ?? EMPTY.alertType }
    );
  }, [isOpen, existing, defaultAlertType]);

  const submit = async () => {
    const next = validateAlertAssignee(values);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    await onSubmit({
      ...values,
      slackUserID: values.slackUserID.trim(),
      slackDisplayName: values.slackDisplayName.trim(),
    });
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="sm"
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {existing ? 'Ubah mention' : 'Tambah mention'}
          </h2>
          <p className="text-sm font-normal text-gray-500">
            Siapa yang di-tag saat notifikasi ini terkirim ke Slack.
          </p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="small"
            onClick={onClose}
            disabled={isSaving}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={submit}
            disabled={isSaving}
          >
            {isSaving ? 'Menyimpan…' : existing ? 'Simpan' : 'Tambah'}
          </Button>
        </div>
      }
    >
      <div className="p-6 space-y-5">
        <div>
          <FieldLabel required>Jenis notifikasi</FieldLabel>
          <SelectField
            value={values.alertType}
            onChange={(value) =>
              setValues((prev) => ({ ...prev, alertType: value as AlertType }))
            }
            options={alertTypes.map((type) => ({
              value: type,
              label: ALERT_TYPE_LABELS[type] ?? type,
            }))}
          />
          <FieldHelper>{ALERT_TYPE_DESCRIPTIONS[values.alertType]}</FieldHelper>
        </div>

        <div>
          <FieldLabel>Business unit</FieldLabel>
          <SelectField
            value={values.businessUnit}
            onChange={(value) =>
              setValues((prev) => ({ ...prev, businessUnit: value }))
            }
            options={[
              { value: '', label: 'Semua (fallback)' },
              ...Object.values(BusinessUnit).map((bu) => ({
                value: bu,
                label: BUSINESS_UNIT_LABELS[bu] || bu,
              })),
            ]}
          />
          <FieldHelper>
            Baris tanpa business unit dipakai untuk unit yang business unit-nya
            belum punya daftar sendiri.
          </FieldHelper>
        </div>

        <div>
          <FieldLabel required>Slack member ID</FieldLabel>
          <input
            type="text"
            value={values.slackUserID}
            onChange={(e) =>
              setValues((prev) => ({
                ...prev,
                slackUserID: e.target.value.toUpperCase(),
              }))
            }
            placeholder="U0123ABCD"
            className={`${inputClass(Boolean(errors.slackUserID))} font-mono`}
          />
          <FieldError>{errors.slackUserID}</FieldError>
          <FieldHelper withIcon>
            Di Slack: buka profil orangnya → tombol ⋯ → "Copy member ID".
          </FieldHelper>
        </div>

        <div>
          <FieldLabel required>Nama tampil</FieldLabel>
          <input
            type="text"
            value={values.slackDisplayName}
            onChange={(e) =>
              setValues((prev) => ({
                ...prev,
                slackDisplayName: e.target.value,
              }))
            }
            placeholder="Nama agar tabel terbaca"
            className={inputClass(Boolean(errors.slackDisplayName))}
          />
          <FieldError>{errors.slackDisplayName}</FieldError>
        </div>

        <div className="flex items-center gap-2">
          <Toggle
            checked={values.isActive}
            onChange={(checked) =>
              setValues((prev) => ({ ...prev, isActive: checked }))
            }
            ariaLabel="Aktif"
          />
          <span className="text-[14px] text-gray-900">Aktif</span>
        </div>
      </div>
    </SideBarModal>
  );
};

export default AlertAssigneeModal;
